import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useCart, getProductMaxCartQuantity } from "../../context/CartContext";
import Seo from "../common/Seo";
import {
  getVariantOptions,
  productHasVariantOptions,
  findVariantOption,
  getLineItemUnitPrice,
  getLineItemListPrice,
} from "../../utils/productVariants";
import { useUserAuth } from "../../context/UserAuthContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { apiClient } from "../../services/apiClientService";
import { trackViewProduct } from "../../analytics/events";
import ReviewForm from "../User/ReviewForm";
import {
  ShoppingBag,
  ShieldCheck,
  Truck,
  RotateCcw,
  Heart,
  Star,
  Minus,
  Plus,
  Share2,
  Copy,
  Smartphone,
} from "lucide-react";

function hashToUnitFloat(seed) {
  const str = String(seed ?? "");
  let h = 2166136261;

  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }

  return (h >>> 0) / 4294967296;
}

const ProductDetails = () => {
  const { productId: productIdParam } = useParams();
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);

  const { cart, addToCart, updateQuantity, removeFromCart } = useCart();
  const { isAuthenticated } = useUserAuth();
  const { wishlistEnabled } = useFeatureFlags();

  const [wishlistIds, setWishlistIds] = useState([]);
  const [wishlistReady, setWishlistReady] = useState(false);

  const [product, setProduct] = useState(state?.product || null);
  const [loading, setLoading] = useState(!state?.product);
  const [error, setError] = useState(null);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSort, setReviewSort] = useState("recent");

  const [toast, setToast] = useState(null);

  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareMenuRef = useRef(null);

  const productShareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const id = product?._id ?? product?.id;
    if (!id) return "";
    return `${window.location.origin}/products/${id}`;
  }, [product?._id, product?.id]);

  useEffect(() => {
    if (!shareMenuOpen) return undefined;

    const onDocMouseDown = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShareMenuOpen(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShareMenuOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [shareMenuOpen]);

  const fallback = useMemo(() => {
    const seed =
      product?._id ??
      product?.id ??
      product?.slug ??
      product?.name ??
      productIdParam;

    const u1 = hashToUnitFloat(seed);
    const u2 = hashToUnitFloat(`${seed}-reviews`);

    const rawRating = 3.8 + u1 * (4.5 - 3.8);
    const rating = Math.round(rawRating * 10) / 10;
    const reviewsCount = 5 + Math.floor(u2 * 16);

    return {
      rating,
      reviews: reviewsCount,
    };
  }, [product?._id, product?.id, product?.slug, product?.name, productIdParam]);

  const reviewList = useMemo(() => {
    const raw = Array.isArray(reviews?.reviews)
      ? reviews.reviews
      : Array.isArray(reviews)
        ? reviews
        : [];

    const cleaned = raw
      .filter(
        (r) =>
          r && typeof r.rating === "number" && r.rating >= 1 && r.rating <= 5,
      )
      .slice();

    cleaned.sort((a, b) => {
      if (reviewSort === "high") {
        return (b.rating ?? 0) - (a.rating ?? 0);
      }

      if (reviewSort === "low") {
        return (a.rating ?? 0) - (b.rating ?? 0);
      }

      const ad = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bd = b?.createdAt ? new Date(b.createdAt).getTime() : 0;

      return bd - ad;
    });

    return cleaned;
  }, [reviews, reviewSort]);

  const ratingCounts = useMemo(() => {
    const counts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };

    for (const r of reviewList) {
      counts[r.rating] += 1;
    }

    return counts;
  }, [reviewList]);

  const variantOpts = useMemo(
    () => (product ? getVariantOptions(product) : []),
    [product],
  );

  const [selectedVariantId, setSelectedVariantId] = useState("");

  useEffect(() => {
    if (!product) return;
    const opts = getVariantOptions(product);
    if (!opts.length) {
      setSelectedVariantId("");
      return;
    }
    setSelectedVariantId((prev) => {
      const keep = opts.some((o) => String(o.optionId) === String(prev));
      return keep ? prev : String(opts[0].optionId);
    });
  }, [product?._id, product?.variantGroup]);

  const cartItem = product
    ? cart.find(
        (item) =>
          item.product._id === product._id &&
          (item.variantOptionId || "") === (selectedVariantId || ""),
      )
    : null;

  const cartQuantity = cartItem ? cartItem.quantity : 0;

  const maxCartQty = product
    ? getProductMaxCartQuantity(product, selectedVariantId)
    : Infinity;
  const hasStockCap = Number.isFinite(maxCartQty);
  const atCartMax = hasStockCap && cartQuantity >= maxCartQty;
  const outOfStock = hasStockCap && maxCartQty <= 0;

  useEffect(() => {
    const segment = String(productIdParam || "");

    const loadProduct = async () => {
      setLoading(true);
      setError(null);

      const sp = state?.product;
      const slugFromName = (p) =>
        String(p?.name || "")
          .toLowerCase()
          .replace(/\s+/g, "-");
      const stateMatchesUrl =
        sp &&
        (String(sp._id ?? sp.id) === segment ||
          slugFromName(sp) === segment.toLowerCase());

      if (stateMatchesUrl) {
        setProduct(sp);
        setLoading(false);
        return;
      }

      try {
        const isObjectId = /^[a-fA-F0-9]{24}$/.test(segment);

        if (isObjectId) {
          const res = await apiClient("GET", `/api/products/${segment}`, null, {
            skipLoading: true,
          });
          const p = res?.product;
          if (!p) throw new Error("Product not found");
          setProduct(p);
          return;
        }

        const res = await apiClient("GET", `/api/products`, null, {
          skipLoading: true,
        });
        const list = Array.isArray(res?.products)
          ? res.products
          : Array.isArray(res)
            ? res
            : [];

        const decodedName = decodeURIComponent(segment).replace(/-/g, " ");

        const foundProduct = list.find(
          (p) => p.name.toLowerCase() === decodedName.toLowerCase(),
        );

        if (!foundProduct) {
          throw new Error("Product not found");
        }

        setProduct(foundProduct);
      } catch (err) {
        setError(err.message);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productIdParam, state]);

  const viewTrackedRef = useRef(null);
  useEffect(() => {
    const id = product?._id ?? product?.id;
    if (!id || viewTrackedRef.current === id) return;
    viewTrackedRef.current = id;
    trackViewProduct(product);
  }, [product]);

  useEffect(() => {
    if (!isAuthenticated || !wishlistEnabled) {
      setWishlistIds([]);
      setWishlistReady(true);
      return;
    }

    let cancelled = false;

    const fetchWishlist = async () => {
      setWishlistReady(false);
      try {
        const res = await apiClient("GET", `/api/wishlist`, null, {
          skipLoading: true,
        });
        if (cancelled) return;
        const list = Array.isArray(res) ? res : [];
        const ids = list.map((item) => String(item._id ?? item));
        setWishlistIds(ids);
      } catch {
        if (!cancelled) setWishlistIds([]);
      } finally {
        if (!cancelled) setWishlistReady(true);
      }
    };

    fetchWishlist();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, wishlistEnabled]);

  useEffect(() => {
    if (!product) return;

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);

        const res = await apiClient(
          "GET",
          `/api/reviews/product/${product._id}`,
        );

        setReviews(res || []);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [product]);

  const showToast = (type, message) => {
    setToast({ type, message });

    window.clearTimeout(showToast._t);

    showToast._t = window.setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  const isWishlisted = product ? wishlistIds.includes(String(product._id)) : false;

  const closeShareMenu = () => setShareMenuOpen(false);

  const copyProductLink = async () => {
    const url =
      productShareUrl ||
      (typeof window !== "undefined" ? window.location.href : "");
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showToast("success", "Link copied to clipboard");
      closeShareMenu();
    } catch {
      showToast("error", "Could not copy link");
    }
  };

  const shareViaDevice = async () => {
    if (!product || typeof navigator === "undefined" || !navigator.share) return;
    const url =
      productShareUrl ||
      (typeof window !== "undefined" ? window.location.href : "");
    try {
      await navigator.share({
        title: product.name,
        text: `Check out ${product.name}`,
        url,
      });
      closeShareMenu();
    } catch (e) {
      if (e?.name === "AbortError") closeShareMenu();
    }
  };

  const openShareWindow = (href) => {
    if (typeof window === "undefined") return;
    window.open(href, "_blank", "noopener,noreferrer");
    closeShareMenu();
  };

  const handleToggleWishlist = async () => {
    if (!product) return;

    if (!isAuthenticated) {
      navigate("/user-login", { state: { from: `${pathname}${search}` } });
      return;
    }

    try {
      const res = await apiClient(
        "POST",
        "/api/wishlist/toggle",
        { productId: product._id },
        { skipLoading: true },
      );
      const raw = res?.wishlist || [];
      const ids = raw.map((id) => String(id));
      setWishlistIds(ids);
      const nowSaved = ids.includes(String(product._id));
      showToast("success", nowSaved ? "Saved to your wishlist" : "Removed from wishlist");
    } catch (e) {
      showToast("error", e?.message || "Could not update wishlist");
    }
  };

  const handleReviewSubmitted = async () => {
    setIsReviewModalOpen(false);

    showToast("success", "Thanks! Your review was submitted.");

    try {
      setReviewsLoading(true);

      const res = await apiClient("GET", `/api/reviews/product/${product._id}`);

      setReviews(res || []);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1,
    );
  };

  if (loading) {
    return (
      <>
        <Seo
          title="Product | Bakery in Patna, Bihar"
          description="View product details and order online in Patna, Bihar."
          canonical={`/products/${encodeURIComponent(productIdParam || "")}`}
          noIndex
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rose-500"></div>
        </div>
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <Seo
          title="Product not found | Bakery in Patna, Bihar"
          description="This product page is not available."
          canonical={`/products/${encodeURIComponent(productIdParam || "")}`}
          noIndex
        />
        <div className="p-8 text-center">
          <h2 className="text-xl font-bold text-rose-600 mb-2">
            Product not found
          </h2>

          <button
            onClick={() => navigate("/")}
            className="mt-4 bg-rose-500 text-white px-4 py-2 rounded hover:bg-rose-600"
          >
            Back to Home
          </button>
        </div>
      </>
    );
  }

  const hasVariants = productHasVariantOptions(product);

  const price = hasVariants
    ? getLineItemListPrice(product, selectedVariantId)
    : Number(product.price) || 0;

  const discountPrice = hasVariants
    ? getLineItemUnitPrice(product, selectedVariantId)
    : Number(product.discountedPrice ?? product.discountPrice ?? price) || price;

  const discountPercent =
    price > 0 && discountPrice < price
      ? Math.round(100 - (discountPrice / price) * 100)
      : 0;

  const reviewsAvg =
    typeof reviews?.avgRating === "number" && reviews.avgRating > 0
      ? reviews.avgRating
      : null;

  const productAvg =
    typeof product?.averageRating === "number" && product.averageRating > 0
      ? product.averageRating
      : null;

  const avgRating = reviewsAvg ?? productAvg ?? fallback.rating;

  const reviewsTotal =
    typeof reviews?.totalReviews === "number" && reviews.totalReviews > 0
      ? reviews.totalReviews
      : null;

  const listCount =
    Array.isArray(reviews?.reviews) && reviews.reviews.length > 0
      ? reviews.reviews.length
      : Array.isArray(reviews) && reviews.length > 0
        ? reviews.length
        : null;

  const productReviews =
    typeof product?.numberOfReviews === "number" && product.numberOfReviews > 0
      ? product.numberOfReviews
      : null;

  const totalReviews =
    reviewsTotal ?? listCount ?? productReviews ?? fallback.reviews;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
      <Seo
        title={`${product.name} | Bakery in Patna, Bihar`}
        description={`Order ${product.name} in Patna, Bihar. Freshly prepared and available for delivery or pickup.`}
        canonical={`/products/${encodeURIComponent(String(product._id ?? product.id ?? productIdParam ?? ""))}`}
        ogImage={product.images?.[0]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: Array.isArray(product.images) ? product.images : undefined,
          description:
            typeof product.description === "string"
              ? product.description.replace(/<[^>]*>/g, " ").trim().slice(0, 240)
              : undefined,
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: Number(discountPrice || 0),
            availability: outOfStock
              ? "https://schema.org/OutOfStock"
              : "https://schema.org/InStock",
            url:
              typeof window === "undefined"
                ? undefined
                : window.location.href,
          },
          brand: { "@type": "Brand", name: "Patna Bakery" },
        }}
      />
      {toast && (
        <div className="fixed top-4 right-4 z-[60]">
          <div
            className={`rounded-2xl px-5 py-3 shadow-2xl border text-sm font-semibold backdrop-blur-md ${
              toast.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white rounded-[32px] p-4 sm:p-8 shadow-xl border border-gray-100">
        <div className="flex flex-col gap-5">
          <div className="relative overflow-hidden rounded-[30px] bg-gradient-to-br from-gray-50 to-gray-100">
            <img
              src={product.images?.[currentIndex]}
              alt={product.name}
              className="w-full h-[380px] sm:h-[520px] object-cover"
            />

            {product.images?.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/90 backdrop-blur-md shadow-lg rounded-full h-11 w-11 flex items-center justify-center hover:scale-105 transition"
                >
                  ←
                </button>

                <button
                  onClick={handleNext}
                  className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/90 backdrop-blur-md shadow-lg rounded-full h-11 w-11 flex items-center justify-center hover:scale-105 transition"
                >
                  →
                </button>
              </>
            )}

            {discountPercent > 0 && (
              <div className="absolute top-5 left-5 bg-rose-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                {discountPercent}% OFF
              </div>
            )}
          </div>

          {product.images?.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {product.images.map((img, idx) => (
                <button
                  key={img}
                  onClick={() => setCurrentIndex(idx)}
                  className={`min-w-[82px] h-[82px] rounded-2xl overflow-hidden border-2 transition-all ${
                    idx === currentIndex
                      ? "border-rose-500 scale-105 shadow-lg"
                      : "border-gray-200 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                {product.type?.map((item) => (
                  <span
                    key={item}
                    className="bg-rose-50 text-rose-600 text-xs font-semibold px-3 py-1.5 rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>

              <h1 className="text-3xl sm:text-4xl font-black text-gray-900 leading-tight">
                {product.name}
              </h1>
            </div>

            <div className="flex shrink-0 gap-2">
              <div className="relative" ref={shareMenuRef}>
                <button
                  type="button"
                  onClick={() => setShareMenuOpen((o) => !o)}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 transition hover:border-orange-200 hover:bg-orange-50"
                  aria-label="Share product"
                  aria-expanded={shareMenuOpen}
                  aria-haspopup="menu"
                >
                  <Share2 className="h-5 w-5 text-orange-600" strokeWidth={1.75} />
                </button>

                {shareMenuOpen ? (
                  <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[240px] overflow-hidden rounded-2xl border border-stone-200 bg-white py-2 shadow-xl shadow-stone-200/80 ring-1 ring-black/5"
                  >
                    {typeof navigator !== "undefined" &&
                    typeof navigator.share === "function" ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={shareViaDevice}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-800 transition hover:bg-stone-50"
                      >
                        <Smartphone className="h-4 w-4 shrink-0 text-stone-500" />
                        Share via apps
                      </button>
                    ) : null}

                    <button
                      type="button"
                      role="menuitem"
                      onClick={copyProductLink}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-800 transition hover:bg-stone-50"
                    >
                      <Copy className="h-4 w-4 shrink-0 text-stone-500" />
                      Copy link
                    </button>

                    <div className="my-1 border-t border-stone-100" />

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        openShareWindow(
                          `https://wa.me/?text=${encodeURIComponent(`${product.name}\n${productShareUrl || window.location.href}`)}`,
                        )
                      }
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-800 transition hover:bg-stone-50"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[10px] font-bold text-emerald-600">
                        WA
                      </span>
                      WhatsApp
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        openShareWindow(
                          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productShareUrl || window.location.href)}`,
                        )
                      }
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-800 transition hover:bg-stone-50"
                    >
                      <span className="text-xs font-bold text-blue-600">f</span>
                      Facebook
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        openShareWindow(
                          `https://twitter.com/intent/tweet?text=${encodeURIComponent(product.name)}&url=${encodeURIComponent(productShareUrl || window.location.href)}`,
                        )
                      }
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-800 transition hover:bg-stone-50"
                    >
                      <span className="text-xs font-bold text-stone-900">X</span>
                      X (Twitter)
                    </button>
                  </div>
                ) : null}
              </div>

              {wishlistEnabled ? (
                <button
                  type="button"
                  onClick={handleToggleWishlist}
                  disabled={!wishlistReady}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray-200 transition hover:border-rose-200 hover:bg-rose-50 disabled:cursor-wait disabled:opacity-60"
                  aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <Heart
                    className={`h-5 w-5 ${isWishlisted ? "fill-rose-500 text-rose-500" : "text-rose-500"}`}
                    strokeWidth={1.75}
                  />
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-5">
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full font-bold">
              <Star className="h-4 w-4 fill-current" />
              {Number(avgRating).toFixed(1)}
            </div>

            <div className="text-gray-500 font-medium">
              {totalReviews} Reviews
            </div>
          </div>

          {variantOpts.length > 0 ? (
            <div className="mt-6">
              <p className="text-sm font-bold text-gray-700 mb-2">
                {product.variantGroup?.name || "Choose an option"}
              </p>
              <div className="flex flex-wrap gap-2">
                {variantOpts.map((opt) => {
                  const id = String(opt.optionId);
                  const active = id === String(selectedVariantId);
                  const soldOut =
                    Math.max(0, Math.floor(Number(opt.quantity) || 0)) <= 0;
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={soldOut}
                      onClick={() => setSelectedVariantId(id)}
                      className={`rounded-2xl border-2 px-4 py-2.5 text-sm font-bold transition ${
                        active
                          ? "border-rose-500 bg-rose-50 text-rose-800 shadow-md"
                          : "border-gray-200 bg-white text-gray-800 hover:border-rose-200"
                      } ${soldOut ? "cursor-not-allowed opacity-45 line-through" : ""}`}
                    >
                      {opt.label}
                      <span className="ml-1.5 tabular-nums text-xs font-semibold text-gray-500">
                        ₹{opt.discountedPrice ?? opt.price}
                      </span>
                      {soldOut ? (
                        <span className="ml-1 text-[10px] uppercase text-rose-600">
                          Sold out
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="mt-7 flex items-end gap-4 flex-wrap">
            <div className="text-4xl font-black text-gray-900">
              ₹{discountPrice}
            </div>

            {price > discountPrice && (
              <div className="flex items-center gap-3">
                <span className="text-xl text-gray-400 line-through">
                  ₹{price}
                </span>

                <span className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-sm">
                  Save {discountPercent}%
                </span>
              </div>
            )}
          </div>

          {product.couponPrice && (
            <div className="mt-4 bg-purple-50 border border-purple-100 text-purple-700 rounded-2xl px-4 py-3 font-semibold">
              Best price ₹{product.couponPrice} with coupon applied
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="flex items-center gap-3 rounded-2xl border border-gray-100 p-4 bg-gray-50">
              <Truck className="h-6 w-6 shrink-0 text-rose-500" />
              <div className="min-w-0">
                <div className="font-bold text-gray-900 text-sm">Fast Delivery</div>
                <div className="text-xs text-gray-500 mt-1">Safe & quick shipping</div>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-gray-100 p-4 bg-gray-50">
              <ShieldCheck className="h-6 w-6 shrink-0 text-green-500" />
              <div className="min-w-0">
                <div className="font-bold text-gray-900 text-sm">Secure Checkout</div>
                <div className="text-xs text-gray-500 mt-1">Trusted payment protection</div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-3xl p-6">
            <div className="flex flex-wrap items-center gap-4 mb-5">
              <div
                className={`px-4 py-2 rounded-full text-sm font-bold ${
                  !outOfStock
                    ? "bg-green-100 text-green-700"
                    : "bg-rose-100 text-rose-700"
                }`}
              >
                {!outOfStock
                  ? hasStockCap
                    ? hasVariants
                      ? `${maxCartQty} available for this option`
                      : `In stock (${maxCartQty} available)`
                    : `In stock${product.quantity > 0 ? ` (${product.quantity})` : ""}`
                  : "Out of stock"}
              </div>

              <div className="text-gray-600 font-medium">
                {Number(product.shippingFee ?? 100) === 0
                  ? "Free Shipping"
                  : `Shipping ₹${Number(product.shippingFee ?? 100)}`}
              </div>
            </div>

            <div
              className="text-gray-700 leading-7 prose max-w-none prose-ul:list-disc prose-li:marker:text-rose-500"
              dangerouslySetInnerHTML={{
                __html: product.description,
              }}
            />

            {cartQuantity > 0 ? (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-between sm:gap-4">
                <div className="flex h-14 min-h-14 w-full min-w-0 max-w-lg items-stretch overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:flex-1">
                  <button
                    type="button"
                    className="flex w-14 shrink-0 items-center justify-center border-r border-gray-100 transition hover:bg-gray-50"
                    onClick={() => {
                      if (cartQuantity > 1) {
                        updateQuantity(
                          product._id,
                          -1,
                          selectedVariantId || "",
                        );
                      } else {
                        removeFromCart(product._id, selectedVariantId || "");
                      }
                    }}
                  >
                    <Minus className="h-5 w-5" />
                  </button>

                  <div className="flex min-w-0 flex-1 items-center justify-center gap-1 bg-gray-50/40 px-3 tabular-nums">
                    <span className="text-xl font-bold leading-none text-gray-900">{cartQuantity}</span>
                    {hasStockCap ? (
                      <span className="text-sm font-semibold leading-none text-gray-500">/ {maxCartQty}</span>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    disabled={atCartMax}
                    title={atCartMax ? `Only ${maxCartQty} in stock` : "Add one more"}
                    className="flex w-14 shrink-0 items-center justify-center border-l border-gray-100 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() =>
                      updateQuantity(product._id, 1, selectedVariantId || "")
                    }
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/checkout")}
                  className="bg-black text-white px-8 py-4 rounded-2xl font-bold hover:bg-gray-900 transition"
                >
                  Go to Cart
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button
                  type="button"
                  disabled={outOfStock}
                  title={outOfStock ? "Out of stock" : "Add to cart"}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-bold py-4 px-6 rounded-2xl transition active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-rose-100 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                  onClick={() =>
                    addToCart(product, {
                      variantOptionId: selectedVariantId || "",
                      variantLabel:
                        findVariantOption(product, selectedVariantId)?.label ||
                        "",
                    })
                  }
                >
                  <ShoppingBag className="h-5 w-5" />
                  {outOfStock ? "Out of stock" : "Add to Cart"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-12 bg-white rounded-[32px] p-4 sm:p-8 shadow-xl border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-black text-gray-900">
              Customer Reviews
            </h2>

            <p className="text-gray-500 mt-1">
              Real experiences from verified buyers
            </p>
          </div>

          <button
            onClick={() => setIsReviewModalOpen(true)}
            className="bg-black text-white px-6 py-3 rounded-2xl text-sm font-bold hover:bg-gray-900 active:scale-95 transition shadow-lg"
          >
            Write a Review
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="rounded-3xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 p-6 shadow-sm">
            <div className="text-5xl font-black text-gray-900">
              {Number(avgRating).toFixed(1)}
            </div>

            <div className="text-yellow-500 text-2xl mt-3">
              {"★".repeat(Math.round(Number(avgRating)))}
              {"☆".repeat(5 - Math.round(Number(avgRating)))}
            </div>

            <div className="text-gray-600 mt-3 font-medium">
              Based on{" "}
              <span className="font-bold text-gray-900">{totalReviews}</span>{" "}
              ratings
            </div>
          </div>

          <div className="lg:col-span-2 rounded-3xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <div className="font-bold text-lg text-gray-900">
                Rating Breakdown
              </div>

              <div className="text-sm text-gray-500">
                {reviewsLoading ? "Loading..." : `${reviewList.length} reviews`}
              </div>
            </div>

            {[5, 4, 3, 2, 1].map((star) => {
              const count = ratingCounts[star];
              const denom = reviewList.length || 1;
              const pct = Math.round((count / denom) * 100);

              return (
                <div key={star} className="flex items-center gap-4 mb-4">
                  <div className="w-10 text-sm font-bold text-gray-800">
                    {star}★
                  </div>

                  <div className="flex-1 h-3 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  <div className="w-20 text-right text-sm text-gray-600 font-medium">
                    {count} ({pct}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="text-gray-800 font-bold text-lg">
            Reviews ({reviewList.length})
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 font-medium">Sort by</span>

            <select
              value={reviewSort}
              onChange={(e) => setReviewSort(e.target.value)}
              className="shop-select-control rounded-2xl border border-stone-200 bg-white py-2.5 pl-4 text-sm font-medium text-stone-800 shadow-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/25"
            >
              <option value="recent">Most Recent</option>
              <option value="high">Highest Rated</option>
              <option value="low">Lowest Rated</option>
            </select>
          </div>
        </div>

        <div className="space-y-5">
          {reviewsLoading ? (
            <div className="space-y-5">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl border border-gray-100 p-6 bg-gradient-to-br from-gray-50 to-white animate-pulse"
                >
                  <div className="flex items-center justify-between">
                    <div className="h-5 w-40 bg-gray-200 rounded-full" />
                    <div className="h-5 w-20 bg-gray-200 rounded-full" />
                  </div>

                  <div className="h-4 w-48 bg-gray-200 rounded-full mt-5" />
                  <div className="h-4 w-full bg-gray-200 rounded-full mt-4" />
                  <div className="h-4 w-5/6 bg-gray-200 rounded-full mt-4" />
                </div>
              ))}
            </div>
          ) : reviewList.length === 0 ? (
            <div className="rounded-3xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-10 text-center shadow-sm">
              <div className="text-2xl font-black text-gray-900 mb-2">
                No reviews yet
              </div>

              <div className="text-gray-500 mb-6">
                Be the first customer to review this product.
              </div>

              <button
                onClick={() => setIsReviewModalOpen(true)}
                className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-bold hover:bg-rose-600 transition"
              >
                Write First Review
              </button>
            </div>
          ) : (
            reviewList.map((review, idx) => (
              <div
                key={review._id || idx}
                className="rounded-3xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm hover:shadow-lg transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center flex-wrap gap-2">
                      <div className="font-bold text-lg text-gray-900">
                        {review.user?.name || "Anonymous"}
                      </div>

                      {!!review.order && (
                        <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold">
                          Verified Purchase
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                      <div className="text-yellow-500">
                        {"★".repeat(review.rating)}
                        {"☆".repeat(5 - review.rating)}
                      </div>

                      <div className="text-sm text-gray-500">
                        {review.createdAt
                          ? new Date(review.createdAt).toLocaleDateString()
                          : ""}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-bold text-gray-700">
                    {review.rating}/5
                  </div>
                </div>

                {review.comment && (
                  <p className="text-gray-700 leading-7 mt-5">
                    {review.comment}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {isReviewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3"
          onClick={() => setIsReviewModalOpen(false)}
        >
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <ReviewForm
              productId={product._id}
              orderId={null}
              onClose={() => setIsReviewModalOpen(false)}
              onSubmitted={handleReviewSubmitted}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
